/**
 * State of ONE tracker's country trend chart: the metric registry filtered to that
 * tracker + SMS, the picked metric and layout, and both ranges' series.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useMemo, useState } from 'react';
import { assertNever, type TrackerId } from '@trafficpulse/shared';

import {
  COMPARE_METRICS,
  TRES_COMMAS_COMPARE_METRICS,
  type CompareMetric,
} from '@/features/dashboard/compare/metrics/compareMetrics';
import type { DashboardDateRange, Granularity } from '@/features/dashboard/compare/data/types';
import type { ChartLayout } from '@/features/dashboard/compare/state/useDashboardCompareState';

import { useCountryCompareSeries } from './useCountryCompareSeries';

export interface TrackerTrendChartProps {
  countryKey: string;
  campaignId: string | undefined;
  provider: TrackerId;
  rangeA: DashboardDateRange;
  rangeB: DashboardDateRange;
  granularity: Granularity;
}

const SMS_METRICS = COMPARE_METRICS.filter((m) => m.source === 'sms');

interface TrendMetrics {
  metrics: CompareMetric[];
  /** FTD (customConversions7) of that tracker — the chart opens on it. */
  defaultKey: string;
}

/** The chart's metric tabs: the tracker's own metrics, then SMS (Voluum/Binom: as before). */
export function trendMetricsFor(provider: TrackerId): TrendMetrics {
  switch (provider) {
    case 'voluum':
    case 'binom':
      return {
        metrics: COMPARE_METRICS.filter((m) => m.source === provider || m.source === 'sms'),
        defaultKey: `${provider}.customConversions7`,
      };
    case 'tres-commas':
      return { metrics: [...TRES_COMMAS_COMPARE_METRICS, ...SMS_METRICS], defaultKey: 'tresCommas.customConversions7' };
    default:
      return assertNever(provider, 'tracker');
  }
}

export function useTrackerTrendChart({ provider, ...query }: TrackerTrendChartProps) {
  const { metrics, defaultKey } = useMemo(() => trendMetricsFor(provider), [provider]);
  const [chartMetricKey, onChartMetric] = useState(defaultKey);
  const [chartLayout, onLayout] = useState<ChartLayout>('single');
  const series = useCountryCompareSeries({ ...query, provider, enabled: true });
  return { metrics, chartMetricKey, onChartMetric, chartLayout, onLayout, series };
}
